import { CircularProgress, Grid, Stack, Typography } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FormContainer } from "./FormContainer";
import { PricePlanContainer } from "./PricePlanContainer";
import { loadStripe } from "@stripe/stripe-js";
import { useSearchParams, Navigate } from "react-router-dom";
import { validPlan, validPlanType } from "./constant";
import { StripeAPI } from "../../../Services/BillingService";
import toast from "react-hot-toast";
import { Context } from "../../../Store/Store";
import { upperFirst } from "lodash";
import countryList from "react-select-country-list";
const FormContext = createContext();
const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null;

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};

export const BillingForm = () => {
  // ======================== STATES
  const [formData, setFormData] = useState({});
  const [planDetails, setPlanDetails] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState({});
  const [loading, setLoading] = useState(true);

  // ======================== HOOKS
  const [searchParams] = useSearchParams();

  // ===================== CONTEXT
  const [state] = useContext(Context);
  const [couponStatus, setCouponStatus] = useState({
    type: null,
    error: null,
  });

  // ===================== VARIABLES
  const plan = searchParams.get("plan");
  const type = searchParams.get("type");
  const isPlanValid = validPlan.includes(plan ?? "");
  const isTypeValid = validPlanType.includes(type ?? "");
  const isValid = isPlanValid && isTypeValid;
  const user = state?.user?.data;
  const countryOptions = useMemo(() => countryList().getData(), []);

  // ==================== FUNCTIONS
  const updateSelectedPlan = (newData) =>
    setSelectedPlan((prev) => ({ ...prev, plan: { ...newData } }));

  const fetchCoupon = useCallback(
    async (couponCode, subscription) => {
      setCouponStatus((prev) => ({
        ...prev,
        type: "loading",
      }));
      try {
        const response = await StripeAPI({
          api_type: "validate_coupon",
          selected_plan: upperFirst(plan),
          coupon_code: couponCode,
          email: user?.email,
        });
        const data = await response.json();
        if (data.planType) {
          const selected = data.planType.find(
            (item) => item.type === subscription?.type
          );
          setSelectedPlan({ plan: { ...selected }, couponCode });
          setPlanDetails(data.planType);
          setCouponStatus({
            type: "success",
            error: null,
          });
        } else {
          setCouponStatus({
            error: data,
            type: "error",
          });
        }
      } catch (error) {
        setCouponStatus({
          error: "Invalid or expired coupon code",
          type: "error",
        });
      }
    },
    [plan, user?.email]
  );

  const fetchPaymentIntent = useCallback(
    async (payload) => {
      setLoading(true);
      try {
        const response = await StripeAPI(payload);
        const data = await response.json();
        const selected = data.planType.find((item) => item.type === type);
        const existingAddress = data?.existingAddress;
        setSelectedPlan({ plan: { ...selected } });
        setPlanDetails(data.planType);
        if (existingAddress) {
          setFormData({
            ...existingAddress,
            country: countryOptions.find(
              (item) => item.value === existingAddress.country
            ),
          });
        }
      } catch (error) {
        toast.error("Error fetching. Please try again later");
      } finally {
        setLoading(false);
      }
    },
    [countryOptions, type]
  );

  // ======================= EFFECTS
  useEffect(() => {
    if (!isValid || !user?.email) return;
    fetchPaymentIntent({
      api_type: "initialization",
      selected_plan: upperFirst(plan),
      email: user?.email,
    });
  }, [fetchPaymentIntent, isValid, plan, user?.email]);

  // ======================= RETURN
  if (!plan || !type || !isValid) {
    return <Navigate to="/Billing" replace />;
  }

  if (loading)
    return (
      <Stack height={"50vh"} justifyContent={"center"} alignItems={"center"}>
        <CircularProgress />
      </Stack>
    );

  if (!stripePromise) {
    return (
      <Stack height={"50vh"} justifyContent={"center"} alignItems={"center"}>
        <Typography color="#1F292E" fontWeight={600}>
          Stripe is not configured.
        </Typography>
        <Typography color="#415058">
          Set `REACT_APP_STRIPE_PUBLIC_KEY` to enable billing checkout.
        </Typography>
      </Stack>
    );
  }

  return (
    <FormContext.Provider
      value={{
        formData,
        planDetails,
        fetchPaymentIntent,
        selectedPlan,
        updateSelectedPlan,
        fetchCoupon,
        couponStatus,
      }}
    >
      <Elements stripe={stripePromise}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            <FormContainer />
          </Grid>
          <Grid item xs={12} md={4}>
            <PricePlanContainer />
          </Grid>
        </Grid>
      </Elements>
    </FormContext.Provider>
  );
};
